import { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Eye, Heart, Trash2, Calendar, Star, Lock, 
  MoreVertical, Share2, Copy, CheckSquare, Square 
} from 'lucide-react';
import { Card, Button, Modal } from '../common';
import { TarotCard } from '../tarot';
import { DeepDiveThread } from './DeepDiveThread';
import { spreads } from '../../data/spreads';
import type { TarotReading } from '../../types/tarot';

interface EnhancedReadingListProps {
  readings: TarotReading[];
  onToggleFavorite: (reading: TarotReading) => Promise<void>;
  onDeleteReading: (id: string) => Promise<void>;
  isLoading?: boolean;
  isSelectionMode?: boolean;
  selectedReadings?: Set<string>;
  onToggleSelection?: (readingId: string) => void;
  onLoadDeepDives?: (parentId: string) => Promise<TarotReading[]>;
}

export default function EnhancedReadingList({
  readings,
  onToggleFavorite,
  onDeleteReading,
  isLoading = false,
  isSelectionMode = false,
  selectedReadings = new Set(),
  onToggleSelection,
  onLoadDeepDives
}: EnhancedReadingListProps) {
  const [selectedReading, setSelectedReading] = useState<TarotReading | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [actionMenuOpen, setActionMenuOpen] = useState<string | null>(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20; // Show 20 readings per page
  
  // Calculate pagination
  const totalPages = Math.ceil(readings.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedReadings = readings.slice(startIndex, endIndex);
  
  // Reset to page 1 when readings change (e.g., filters applied)
  const [prevReadingsLength, setPrevReadingsLength] = useState(readings.length);
  if (readings.length !== prevReadingsLength) {
    setPrevReadingsLength(readings.length);
    if (currentPage > 1) {
      setCurrentPage(1);
    }
  }

  const getSpreadName = (spreadId: string) => {
    // Map Gemini spread keys to display names
    const geminiSpreadNames: Record<string, string> = {
      'daily_1': 'Daily Card',
      'choice_2': 'Choice Spread',
      'timeline_3': 'Timeline',
      'celtic_10': 'Celtic Cross',
      'deep_dive': 'Deep Dive',
    };
    if (geminiSpreadNames[spreadId]) return geminiSpreadNames[spreadId];
    const spread = spreads.find(s => s.id === spreadId);
    return spread?.name || spreadId || 'Unknown Spread';
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'Unknown date';
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return 'Unknown date';
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatRelativeDate = (dateString: string | undefined) => {
    if (!dateString) return 'Unknown date';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Unknown date';
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    
    const diffInWeeks = Math.floor(diffInDays / 7);
    if (diffInWeeks < 4) return `${diffInWeeks}w ago`;
    
    return formatDate(dateString);
  };

  const handleCopyLink = async (reading: TarotReading) => {
    try {
      const url = `${window.location.origin}/reading/${reading.id}`;
      await navigator.clipboard.writeText(url);
      // You could add a toast notification here
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
    setActionMenuOpen(null);
  };

  const handleShare = async (reading: TarotReading) => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Tarot Reading: ${reading.question}`,
          text: `Check out my tarot reading about "${reading.question}"`,
          url: `${window.location.origin}/reading/${reading.id}`
        });
      } catch (error) {
        console.error('Failed to share:', error);
      }
    } else {
      handleCopyLink(reading);
    }
    setActionMenuOpen(null);
  };

  if (readings.length === 0) {
    return (
      <Card className="text-center py-12">
        <div className="text-6xl mb-4">🎴</div>
        <h3 className="text-xl font-semibold text-white mb-2">
          No Readings Found
        </h3>
        <p className="text-gray-400 mb-6">
          Try adjusting your search filters or create a new reading
        </p>
        <Link to="/reading">
          <Button size="lg">
            Create New Reading
          </Button>
        </Link>
      </Card>
    );
  }

  return (
    <>
      <div className="space-y-4">
        {paginatedReadings.map((reading) => {
          const isSelected = selectedReadings.has(reading.id);
          
          return (
            <Card 
              key={reading.id} 
              className={`hover:bg-dark-750 transition-all duration-200 group relative ${
                isSelected ? 'ring-2 ring-primary-500 bg-primary-500/10' : ''
              }`}
            >
              <div className="flex items-start justify-between">
                {/* Selection Checkbox */}
                {isSelectionMode && (
                  <div className="flex-shrink-0 mr-3 mt-1">
                    <button
                      onClick={() => onToggleSelection?.(reading.id)}
                      className="p-1 hover:bg-dark-700 rounded transition-colors"
                    >
                      {isSelected ? (
                        <CheckSquare className="h-5 w-5 text-primary-500" />
                      ) : (
                        <Square className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                  </div>
                )}
              {/* Reading Content */}
              <div className="flex-1 min-w-0">
                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-white truncate text-lg">
                        {reading.question}
                      </h3>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {reading.isFavorite && (
                          <Heart className="h-4 w-4 text-red-500 fill-current" />
                        )}
                        {reading.isPrivate && (
                          <Lock className="h-4 w-4 text-purple-500" />
                        )}
                        {reading.status === 'completed' && reading.aiInterpretation && (
                          <Star className="h-4 w-4 text-primary-500" />
                        )}
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {formatRelativeDate(reading.createdAt)}
                      </span>
                      <span className="px-2 py-1 bg-dark-600 rounded text-xs">
                        {getSpreadName(reading.spreadId)}
                      </span>
                      <span>{reading.cards.length} cards</span>
                      <span className={`px-2 py-1 rounded text-xs ${
                        reading.status === 'completed' 
                          ? 'bg-green-500/20 text-green-400' 
                          : reading.status === 'pending'
                          ? 'bg-yellow-500/20 text-yellow-400'
                          : 'bg-red-500/20 text-red-400'
                      }`}>
                        {reading.status}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Tags */}
                {reading.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {reading.tags.slice(0, 3).map((tag, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-primary-500/20 text-primary-400 text-xs rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                    {reading.tags.length > 3 && (
                      <span className="px-2 py-1 bg-dark-600 text-gray-400 text-xs rounded-full">
                        +{reading.tags.length - 3} more
                      </span>
                    )}
                  </div>
                )}

                {/* Card Preview */}
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {reading.cards.slice(0, 6).map((card, index) => (
                    <div key={index} className="flex-shrink-0">
                      <TarotCard
                        card={card}
                        isRevealed={true}
                        size="xs"
                        showMeaning={false}
                      />
                    </div>
                  ))}
                  {reading.cards.length > 6 && (
                    <div className="flex-shrink-0 w-8 h-12 bg-dark-600 rounded flex items-center justify-center text-xs text-gray-400">
                      +{reading.cards.length - 6}
                    </div>
                  )}
                </div>

                {/* Interpretation Preview */}
                {reading.interpretation && (
                  <div className="mt-3 p-3 bg-dark-800 rounded-lg">
                    <p className="text-sm text-gray-300 line-clamp-2">
                      {reading.interpretation.length > 150 
                        ? `${reading.interpretation.substring(0, 150)}...`
                        : reading.interpretation
                      }
                    </p>
                  </div>
                )}

                {/* Deep Dive Thread */}
                {onLoadDeepDives && reading.deepDiveCount && reading.deepDiveCount > 0 && (
                  <DeepDiveThread
                    parentReading={reading}
                    deepDiveCount={reading.deepDiveCount}
                    onLoadDeepDives={onLoadDeepDives}
                    onViewReading={setSelectedReading}
                  />
                )}
              </div>

              {/* Actions */}
              <div className="flex flex-col gap-2 ml-4 relative">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setSelectedReading(reading)}
                  className="opacity-70 group-hover:opacity-100 transition-opacity"
                >
                  <Eye className="h-4 w-4" />
                </Button>

                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onToggleFavorite(reading)}
                  disabled={isLoading}
                  className="opacity-70 group-hover:opacity-100 transition-opacity"
                >
                  <Heart className={`h-4 w-4 ${
                    reading.isFavorite ? 'text-red-500 fill-current' : 'text-gray-400'
                  }`} />
                </Button>

                <div className="relative">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setActionMenuOpen(
                      actionMenuOpen === reading.id ? null : reading.id
                    )}
                    className="opacity-70 group-hover:opacity-100 transition-opacity"
                  >
                    <MoreVertical className="h-4 w-4" />
                  </Button>

                  {/* Action Menu */}
                  {actionMenuOpen === reading.id && (
                    <div className="absolute right-0 top-full mt-1 bg-dark-800 border border-dark-600 rounded-lg shadow-lg z-10 min-w-[160px]">
                      <button
                        onClick={() => handleShare(reading)}
                        className="w-full px-3 py-2 text-left text-sm text-gray-300 hover:bg-dark-700 flex items-center gap-2"
                      >
                        <Share2 className="h-3 w-3" />
                        Share
                      </button>
                      <button
                        onClick={() => handleCopyLink(reading)}
                        className="w-full px-3 py-2 text-left text-sm text-gray-300 hover:bg-dark-700 flex items-center gap-2"
                      >
                        <Copy className="h-3 w-3" />
                        Copy Link
                      </button>
                      <button
                        onClick={() => {
                          setDeleteConfirm(reading.id);
                          setActionMenuOpen(null);
                        }}
                        className="w-full px-3 py-2 text-left text-sm text-red-400 hover:bg-dark-700 flex items-center gap-2"
                      >
                        <Trash2 className="h-3 w-3" />
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Card>
        );
        })}
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="mt-8 flex items-center justify-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            Previous
          </Button>
          
          <div className="flex items-center gap-2">
            {/* Show first page */}
            {currentPage > 3 && (
              <>
                <Button
                  variant={currentPage === 1 ? 'primary' : 'ghost'}
                  size="sm"
                  onClick={() => setCurrentPage(1)}
                  className="min-w-[40px]"
                >
                  1
                </Button>
                {currentPage > 4 && (
                  <span className="text-gray-400 px-2">...</span>
                )}
              </>
            )}
            
            {/* Show pages around current page */}
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(page => {
                // Show pages within 2 of current page
                return Math.abs(page - currentPage) <= 2;
              })
              .map(page => (
                <Button
                  key={page}
                  variant={currentPage === page ? 'primary' : 'ghost'}
                  size="sm"
                  onClick={() => setCurrentPage(page)}
                  className="min-w-[40px]"
                >
                  {page}
                </Button>
              ))}
            
            {/* Show last page */}
            {currentPage < totalPages - 2 && (
              <>
                {currentPage < totalPages - 3 && (
                  <span className="text-gray-400 px-2">...</span>
                )}
                <Button
                  variant={currentPage === totalPages ? 'primary' : 'ghost'}
                  size="sm"
                  onClick={() => setCurrentPage(totalPages)}
                  className="min-w-[40px]"
                >
                  {totalPages}
                </Button>
              </>
            )}
          </div>
          
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
          >
            Next
          </Button>
          
          <span className="text-sm text-gray-400 ml-4">
            Page {currentPage} of {totalPages}
          </span>
        </div>
      )}

      {/* Reading Detail Modal */}
      {selectedReading && (
        <Modal
          isOpen={true}
          onClose={() => setSelectedReading(null)}
          title="Reading Details"
          size="lg"
        >
          <div className="space-y-6">
            {/* Question and Meta */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-lg font-semibold text-white">
                  {selectedReading.question}
                </h3>
                {selectedReading.isFavorite && (
                  <Heart className="h-4 w-4 text-red-500 fill-current" />
                )}
                {selectedReading.isPrivate && (
                  <Lock className="h-4 w-4 text-purple-500" />
                )}
              </div>
              <div className="flex flex-wrap gap-4 text-sm text-gray-400">
                <span>{formatDate(selectedReading.createdAt)}</span>
                <span>{getSpreadName(selectedReading.spreadId)}</span>
                <span>{selectedReading.cards.length} cards</span>
                <span className={`px-2 py-1 rounded text-xs ${
                  selectedReading.status === 'completed' 
                    ? 'bg-green-500/20 text-green-400' 
                    : 'bg-yellow-500/20 text-yellow-400'
                }`}>
                  {selectedReading.status}
                </span>
              </div>
            </div>

            {/* Cards */}
            <div>
              <h4 className="font-semibold text-white mb-3">Cards Drawn</h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {selectedReading.cards.map((card, index) => (
                  <div key={index} className="text-center">
                    <TarotCard
                      card={card}
                      isRevealed={true}
                      size="sm"
                      showMeaning={false}
                    />
                    <div className="mt-2 text-xs text-gray-400">
                      Position {card.position + 1}
                      {card.isReversed && ' (Reversed)'}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Interpretation */}
            {selectedReading.interpretation && (
              <div>
                <h4 className="font-semibold text-white mb-3">Interpretation</h4>
                <div className="bg-dark-800 rounded-lg p-4 text-gray-300 text-sm whitespace-pre-wrap">
                  {selectedReading.interpretation}
                </div>
              </div>
            )}

            {/* AI Interpretation */}
            {selectedReading.aiInterpretation && (
              <div>
                <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                  <Star className="h-4 w-4 text-primary-500" />
                  AI Interpretation
                </h4>
                <div className="bg-primary-500/10 border border-primary-500/20 rounded-lg p-4 text-gray-300 text-sm whitespace-pre-wrap">
                  {selectedReading.aiInterpretation}
                </div>
              </div>
            )}

            {/* Tags */}
            {selectedReading.tags.length > 0 && (
              <div>
                <h4 className="font-semibold text-white mb-3">Tags</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedReading.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-primary-500/20 text-primary-400 text-sm rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-between pt-4 border-t border-dark-700">
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  onClick={() => onToggleFavorite(selectedReading)}
                  disabled={isLoading}
                >
                  <Heart className={`h-4 w-4 mr-2 ${
                    selectedReading.isFavorite ? 'text-red-500 fill-current' : ''
                  }`} />
                  {selectedReading.isFavorite ? 'Remove Favorite' : 'Add Favorite'}
                </Button>
                
                <Button
                  variant="secondary"
                  onClick={() => handleShare(selectedReading)}
                >
                  <Share2 className="h-4 w-4 mr-2" />
                  Share
                </Button>
              </div>

              <Button
                variant="ghost"
                onClick={() => setSelectedReading(null)}
              >
                Close
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <Modal
          isOpen={true}
          onClose={() => setDeleteConfirm(null)}
          title="Delete Reading"
        >
          <div className="space-y-4">
            <p className="text-gray-300">
              Are you sure you want to delete this reading? This action cannot be undone.
            </p>

            <div className="flex justify-end gap-3">
              <Button
                variant="secondary"
                onClick={() => setDeleteConfirm(null)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  onDeleteReading(deleteConfirm);
                  setDeleteConfirm(null);
                }}
                disabled={isLoading}
                className="bg-red-600 hover:bg-red-700"
              >
                {isLoading ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Click outside to close action menu */}
      {actionMenuOpen && (
        <div 
          className="fixed inset-0 z-5"
          onClick={() => setActionMenuOpen(null)}
        />
      )}
    </>
  );
}